import {
  forwardRef,
  useState,
  useRef,
  useCallback,
  useEffect,
  ComponentProps,
  RefObject,
} from "react";
import OutlinedTabItem from "./OutlinedTabItem";
import TextTabItem from "./TextTabItem";
import _ from "lodash";
import { TabItemProps } from "./TabItemProps";
import { cn } from "@/lib/utils";
import { useCourseEditorContext } from "../context";
import { tabItemClasses } from "@/classes/tabItemClasses";

const TabItemRoot = (props: ComponentProps<"div">) => {
  return (
    <div
      {...props}
      className={cn(
        "bg-paper group/tab relative opacity-60 has-data-[state=active]:opacity-100",
        props.className,
      )}
    />
  );
};

export const CourseTabItem = forwardRef<HTMLButtonElement, TabItemProps>((props, ref) => {
  const { path, text } = props as TabItemProps;
  const { courseData, setValue } = useCourseEditorContext();

  const [isRenaming, setIsRenaming] = useState(false);
  const [newName, setNewName] = useState("");
  const renameInputRef = useRef<null | HTMLInputElement | HTMLTextAreaElement>(null);

  const startRenaming = useCallback(() => {
    setNewName(text);
    setIsRenaming(true);
  }, [setIsRenaming, setNewName, text]);

  const endRenaming = useCallback(
    (options?: { singleTitle?: boolean; cancel?: boolean }) => {
      const { singleTitle, cancel } = options || {};
      setIsRenaming(false);
      if (cancel) {
        return;
      }

      const name = newName.trim();
      if (newName === text || name.length === 0) {
        return;
      }

      if (singleTitle) {
        setValue(path.concat(["title"]), name, {
          mustExist: false,
        });
      } else {
        setValue(path.concat(["titles", _.get(courseData, path).selectedTitle]), newName);
      }
    },
    [setIsRenaming, setValue, newName, path, courseData, text],
  );

  useEffect(() => {
    if (isRenaming && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [isRenaming]);

  const childProps = {
    ...props,
    newName,
    startRenaming,
    endRenaming,
    isRenaming,
    setNewName,
  };

  return (
    <TabItemRoot className={cn(tabItemClasses.root, props.isSelected && tabItemClasses.selected)}>
      {props.variant === "outlined" ? (
        <OutlinedTabItem
          ref={ref}
          {...childProps}
          renameInputRef={renameInputRef as RefObject<HTMLTextAreaElement>}
        />
      ) : (
        <TextTabItem
          ref={ref}
          {...childProps}
          renameInputRef={renameInputRef as RefObject<HTMLInputElement>}
        />
      )}
    </TabItemRoot>
  );
});

export default CourseTabItem;
